import React, { useState, useEffect } from 'react';
import {
  Loader2, AlertCircle, BarChart3, Calendar, TrendingUp,
  Map, Search, MapPin, Route, Star, Heart, Share2, Download,
  Navigation, Users, Filter, CreditCard
} from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../services/firebaseService';

interface FeatureMeta {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
}

const FEATURE_MAP: Record<string, FeatureMeta> = {
  map_view:         { label: 'Kartvisninger',     color: 'text-indigo-400',  bgColor: 'bg-indigo-500', icon: Map },
  search:           { label: 'Søk',               color: 'text-violet-400',  bgColor: 'bg-violet-500', icon: Search },
  spot_visit:       { label: 'Plassbesøk',        color: 'text-emerald-400', bgColor: 'bg-emerald-500', icon: MapPin },
  route_plan:       { label: 'Ruteplanlegging',    color: 'text-blue-400',    bgColor: 'bg-blue-500', icon: Route },
  review_write:     { label: 'Anmeldelser',        color: 'text-amber-400',   bgColor: 'bg-amber-500', icon: Star },
  favorite_add:     { label: 'Favoritter',         color: 'text-pink-400',    bgColor: 'bg-pink-500', icon: Heart },
  share:            { label: 'Delinger',           color: 'text-cyan-400',    bgColor: 'bg-cyan-500', icon: Share2 },
  offline_download: { label: 'Offline kart',       color: 'text-[var(--text-secondary)]',   bgColor: 'bg-stone-500', icon: Download },
  trip_log:         { label: 'Turlogger',          color: 'text-lime-400',    bgColor: 'bg-lime-500', icon: Navigation },
  article_read:     { label: 'Artikler lest',      color: 'text-orange-400',  bgColor: 'bg-orange-500', icon: BarChart3 },
  collab_map_view:  { label: 'Samarbeidsmappe',     color: 'text-purple-400',  bgColor: 'bg-purple-500', icon: Users },
  filter_change:    { label: 'Filterendringer',    color: 'text-teal-400',    bgColor: 'bg-teal-500', icon: Filter },
};

interface PaidUserInfo {
  total: number;
  paid: number;
  providers: Record<string, number>;
}

interface UsageStats {
  totals: Record<string, number>;
  last7: Record<string, number>;
  last30: Record<string, number>;
  dailyTrend: Array<{ date: string; total: number }>;
  paidInfo: PaidUserInfo;
}

const AdminUsageDashboard: React.FC = () => {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { fetchUsageStats(); }, []);

  const fetchUsageStats = async () => {
    setLoading(true);
    try {
      const [snap, usersSnap] = await Promise.all([
        getDocs(collection(db, 'usage_stats')),
        getDocs(query(collection(db, 'users'), orderBy('creationTimestamp', 'desc'))),
      ]);

      const totals: Record<string, number> = {};
      const last7: Record<string, number> = {};
      const last30: Record<string, number> = {};
      const dailyMap: Record<string, number> = {};

      const d7 = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      const d30 = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

      snap.docs.forEach(docSnap => {
        const date = docSnap.id;
        const data = docSnap.data();
        let dayTotal = 0;

        for (const [key, val] of Object.entries(data)) {
          if (typeof val !== 'number') continue;
          totals[key] = (totals[key] || 0) + val;
          dayTotal += val;
          if (date >= d7) last7[key] = (last7[key] || 0) + val;
          if (date >= d30) last30[key] = (last30[key] || 0) + val;
        }

        if (date >= d30) dailyMap[date] = dayTotal;
      });

      const dailyTrend = Object.entries(dailyMap)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, total]) => ({ date, total }));

      // Paid user info
      const providers: Record<string, number> = {};
      let paidCount = 0;
      usersSnap.docs.forEach(d => {
        const data = d.data();
        if (data.subscriptionStatus === 'paid' && !data.isTester && !data.subscriptionSandbox) {
          paidCount++;
          const prov = (data.subscriptionProvider as string) || 'ukjent';
          providers[prov] = (providers[prov] || 0) + 1;
        }
      });

      setStats({ totals, last7, last30, dailyTrend, paidInfo: { total: usersSnap.size, paid: paidCount, providers } });
    } catch (e: any) {
      setError(e.message || 'Kunne ikke hente bruksstatistikk');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster bruksdata...</p>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-4">
        <AlertCircle className="text-red-400 shrink-0" size={18} />
        <div>
          <p className="text-sm font-bold text-red-400">Bruksstatistikk utilgjengelig</p>
          <p className="text-xs text-red-400/70 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const totalSum = Object.values(stats.totals).reduce((a, b) => a + b, 0);

  if (totalSum === 0) {
    return (
      <div className="p-8 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel text-center">
        <BarChart3 size={32} className="text-[var(--text-muted)] mx-auto mb-4" />
        <p className="text-sm font-bold text-[var(--text-secondary)]">Ingen bruksdata ennå</p>
        <p className="text-xs text-[var(--text-muted)] mt-2">
          Data samles automatisk fra nå. Fullstendig statistikk vises etter noen dagers bruk.
        </p>
      </div>
    );
  }

  const sortedFeatures = Object.entries(stats.totals)
    .filter(([k]) => FEATURE_MAP[k])
    .sort((a, b) => b[1] - a[1]);
  const maxCount = sortedFeatures[0]?.[1] || 1;

  const sortedLast7 = Object.entries(stats.last7)
    .filter(([k]) => FEATURE_MAP[k])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const sortedLast30 = Object.entries(stats.last30)
    .filter(([k]) => FEATURE_MAP[k])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const todayKey = new Date().toISOString().split('T')[0];
  const todayData = stats.dailyTrend.find(d => d.date === todayKey);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <BarChart3 size={16} className="text-violet-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Totalt alle tider</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{totalSum.toLocaleString('nb-NO')}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">hendelser registrert</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <Calendar size={16} className="text-indigo-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Siste 7 dager</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">
            {Object.values(stats.last7).reduce((a, b) => a + b, 0).toLocaleString('nb-NO')}
          </p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">hendelser</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp size={16} className="text-emerald-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">I dag</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{(todayData?.total || 0).toLocaleString('nb-NO')}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">hendelser</p>
        </div>
      </div>

      {/* Paid users insight */}
      {stats.paidInfo.paid > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-panel p-6">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <CreditCard size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-lg font-black text-[var(--text-primary)]">Betalende brukere</p>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                {stats.paidInfo.paid} av {stats.paidInfo.total} brukere ({(stats.paidInfo.paid / stats.paidInfo.total * 100).toFixed(1)}% konvertering)
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.paidInfo.providers).sort((a, b) => b[1] - a[1]).map(([prov, count]) => (
              <div key={prov} className="bg-amber-500/10 border border-amber-500/20 px-4 py-2 rounded-2xl">
                <p className="text-xs font-black text-amber-300 capitalize">{prov}</p>
                <p className="text-lg font-black text-[var(--text-primary)]">{count}</p>
              </div>
            ))}
          </div>
          <p className="text-2xs text-[var(--text-muted)] mt-4">
            Bruksstatistikk er aggregert — per-bruker filtrering krever per-bruker hendelseslogging.
          </p>
        </div>
      )}

      {/* Feature ranking */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-violet-500/10 flex items-center justify-center">
            <BarChart3 size={20} className="text-violet-400" />
          </div>
          <div>
            <p className="text-lg font-black text-[var(--text-primary)]">Funksjonsbruk</p>
            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Rangert etter total bruk</p>
          </div>
        </div>

        <div className="space-y-3">
          {sortedFeatures.map(([key, count]) => {
            const meta = FEATURE_MAP[key];
            if (!meta) return null;
            const Icon = meta.icon;
            const pct = (count / maxCount) * 100;
            return (
              <div key={key} className="flex items-center gap-4">
                <div className="w-32 flex items-center gap-2 shrink-0">
                  <Icon size={14} className={`${meta.color} shrink-0`} />
                  <span className="text-2xs font-bold text-[var(--text-secondary)] truncate">{meta.label}</span>
                </div>
                <div className="flex-1 bg-[var(--bg-input)] h-6 rounded-lg overflow-hidden">
                  <div
                    className={`${meta.bgColor}/30 h-full rounded-lg transition-all duration-700`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-sm font-black text-[var(--text-primary)] w-16 text-right shrink-0">
                  {count.toLocaleString('nb-NO')}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top features last 7 days and last 30 days */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6">
          <p className="text-sm font-black text-[var(--text-primary)] mb-4">Topp 5 – Siste 7 dager</p>
          <div className="space-y-3">
            {sortedLast7.map(([key, count], i) => {
              const meta = FEATURE_MAP[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-2xs font-black text-[var(--text-muted)] w-4">{i + 1}</span>
                  <Icon size={14} className={meta.color} />
                  <span className="text-xs font-bold text-[var(--text-secondary)] flex-1 truncate">{meta.label}</span>
                  <span className="text-xs font-black text-[var(--text-primary)]">{count.toLocaleString('nb-NO')}</span>
                </div>
              );
            })}
            {sortedLast7.length === 0 && (
              <p className="text-2xs text-[var(--text-muted)]">Ingen data siste 7 dager</p>
            )}
          </div>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6">
          <p className="text-sm font-black text-[var(--text-primary)] mb-4">Topp 5 – Siste 30 dager</p>
          <div className="space-y-3">
            {sortedLast30.map(([key, count], i) => {
              const meta = FEATURE_MAP[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-2xs font-black text-[var(--text-muted)] w-4">{i + 1}</span>
                  <Icon size={14} className={meta.color} />
                  <span className="text-xs font-bold text-[var(--text-secondary)] flex-1 truncate">{meta.label}</span>
                  <span className="text-xs font-black text-[var(--text-primary)]">{count.toLocaleString('nb-NO')}</span>
                </div>
              );
            })}
            {sortedLast30.length === 0 && (
              <p className="text-2xs text-[var(--text-muted)]">Ingen data siste 30 dager</p>
            )}
          </div>
        </div>
      </div>

      {/* Daily trend chart */}
      {stats.dailyTrend.length > 0 && (
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
              <Calendar size={20} className="text-indigo-400" />
            </div>
            <div>
              <p className="text-lg font-black text-[var(--text-primary)]">Daglig aktivitet</p>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">Siste 30 dager – alle hendelser</p>
            </div>
          </div>

          <div className="h-48 flex items-end gap-1 px-2">
            {(() => {
              const max = Math.max(...stats.dailyTrend.map(d => d.total), 1);
              return stats.dailyTrend.map((item, idx) => {
                const height = (item.total / max) * 100;
                return (
                  <div key={item.date} className="flex-1 h-full group relative flex flex-col items-center justify-end">
                    <div
                      className={`w-full rounded-t-lg transition-all duration-500 relative min-h-[4px] ${
                        item.total > 0
                          ? 'bg-indigo-500/20 group-hover:bg-indigo-500/40'
                          : 'bg-[var(--bg-input)]/30'
                      }`}
                      style={{ height: `${Math.max(height, 3)}%` }}
                    >
                      {item.total > 0 && (
                        <>
                          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[7px] font-black text-[var(--text-secondary)] whitespace-nowrap z-10">
                            {item.total}
                          </div>
                          <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-3xs font-black px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                            {item.date.split('-').slice(1).reverse().join('/')}: {item.total}
                          </div>
                        </>
                      )}
                    </div>
                    {(idx % 5 === 0 || idx === stats.dailyTrend.length - 1) && (
                      <span className="text-[7px] text-[var(--text-muted)] font-black mt-2 absolute -bottom-5 whitespace-nowrap">
                        {item.date.split('-').slice(1).reverse().join('/')}
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsageDashboard;
